import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuthContext } from "../../../contexts/AuthContext";
import Sidebar from "../../UI/Sidebar";
import { LoadingProgress, ChartCard } from "../Common";
import { useSequentialLoading } from "../Common/hooks/useSequentialLoading";
import SimpleStatCard from "../Common/components/SimpleStatCard";
import UserFilter from "../Common/components/UserFilter";
import SkeletonLoader from "../../UI/SkeletonLoader";
import {
  DMPieChart,
  DMStackedChart,
  greenColor,
  orangeColor,
  redColor,
  yellowColor,
  multicolor,
} from "../../UI/DMCharts";
import { openURLInNewTab } from "../../Utils";
import * as queries from "./fulfillmentQueries";
import Loader from "../../UI/Loader";
import ErrorMessage from "../../UI/ErrorMessage";
import {
  getWebsitesLaunchedThisMonth,
  getWebsitesInProgress,
  getWebsitesWithRoadblocks,
  getWebsiteUpdatesThisMonth,
  getSocialMediaPostsThisMonth,
  getSocialMediaPostsOverdue,
  getPPCBingRoadblocks,
  getSocialPlusNextdoorRoadblocks,
  getContentUploadedThisMonth,
  getContentCompletedThisMonth,
  getContentOrdersStatus,
  getContentOrdersOverdue,
  getUsersList,
  getOpenTasks,
} from "./fulfillmentQueries";

const FulfillmentDashboard = () => {
  const { type, owId, sidebar, hidediv } = useParams();
  const navigate = useNavigate();
  const context = useAuthContext();

  // Add render count tracking for debugging
  const renderCountRef = useRef(0);
  renderCountRef.current += 1;

  // State for user filtering
  const [selectedUser, setSelectedUser] = useState(owId || null);
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  // State for open tasks (loaded separately)
  const [openTasks, setOpenTasks] = useState([]);
  const [loadingOpenTasks, setLoadingOpenTasks] = useState(false);

  // Use refs to store stable chart data and prevent re-renders
  const chartDataRef = useRef({});
  const lastDataHashRef = useRef("");

  // Effect to update selectedUser when owId changes
  useEffect(() => {
    if (owId !== undefined) {
      setSelectedUser(owId === "0" ? null : owId);
    }
  }, [owId]);

  // Debug effect for tracking re-renders - only log when state changes
  useEffect(() => {
    console.log("🎨 FulfillmentDashboard: Component state changed", {
      renderCount: renderCountRef.current,
      selectedUser,
      contextReady: context?.ready,
      usersCount: users.length,
    });
  }, [selectedUser, context?.ready, users.length]);

  // Load users list when context is ready
  useEffect(() => {
    if (!context?.ready || users.length > 0) return;

    setLoadingUsers(true);
    getUsersList(context)
      .then((result) => {
        setUsers(result);
      })
      .catch(console.error)
      .finally(() => setLoadingUsers(false));
  }, [context?.ready, users.length]);

  // Memoize queryParams to prevent unnecessary re-renders
  const queryParams = useMemo(
    () => ({
      selectedUser,
      type,
    }),
    [selectedUser, type]
  );

  const {
    data,
    loading,
    error,
    progress,
    handleRetry,
    isLoading,
    isInitialized,
  } = useSequentialLoading({
    queries: {
      websitesLaunchedThisMonth: getWebsitesLaunchedThisMonth,
      websitesInProgress: getWebsitesInProgress,
      websitesWithRoadblocks: getWebsitesWithRoadblocks,
      websiteUpdatesThisMonth: getWebsiteUpdatesThisMonth,
      socialMediaPostsThisMonth: getSocialMediaPostsThisMonth,
      socialMediaPostsOverdue: getSocialMediaPostsOverdue,
      ppcBingRoadblocks: getPPCBingRoadblocks,
      socialPlusNextdoorRoadblocks: getSocialPlusNextdoorRoadblocks,
      contentUploadedThisMonth: getContentUploadedThisMonth,
      contentCompletedThisMonth: getContentCompletedThisMonth,
      contentOrdersStatus: getContentOrdersStatus,
      contentOrdersOverdue: getContentOrdersOverdue,
    },
    context: context,
    queryParams: queryParams,
  });

  // Debug logging - only log when loading state changes
  useEffect(() => {
    if (isInitialized && !isLoading) {
      console.log("✅ FulfillmentDashboard: All queries completed", {
        dataKeys: Object.keys(data || {}),
        errorKeys: Object.keys(error || {}),
        renderCount: renderCountRef.current,
      });
    }
  }, [isInitialized, isLoading]);

  // Check if ALL data is ready
  const isAllDataReady = useMemo(() => {
    const mainDataReady = isInitialized && !isLoading;
    const allReady = mainDataReady;

    console.log("📊 Fulfillment Dashboard - Data readiness check:", {
      mainDataReady,
      allReady,
      renderCount: renderCountRef.current,
    });

    return allReady;
  }, [isInitialized, isLoading]);

  // Load open tasks separately after main dashboard loads
  useEffect(() => {
    if (!context?.ready || !isAllDataReady) return;

    const loadOpenTasks = async () => {
      setLoadingOpenTasks(true);
      try {
        const tasks = await getOpenTasks(context, selectedUser);
        setOpenTasks(tasks);
      } catch (error) {
        console.error("Error loading open tasks:", error);
        setOpenTasks([]);
      } finally {
        setLoadingOpenTasks(false);
      }
    };

    const timeoutId = setTimeout(loadOpenTasks, 500);
    return () => clearTimeout(timeoutId);
  }, [context?.ready, isAllDataReady, selectedUser]);

  // Memoized event handlers
  const handleUserChange = useCallback((userId) => {
    setSelectedUser(userId);
  }, []);

  // Handle chart click events
  const handleChartClick = useCallback((queryKey, segment) => {
    console.log("Chart clicked:", queryKey, segment);
    // Add any click handling logic here
  }, []);

  // Show loading state
  if (!context?.ready || !isAllDataReady) {
    return (
      <div style={{ height: "100vh", overflow: "auto" }}>
        <div className="container">
          <Sidebar activeItem="fulfillment" />
          <div className="content dashboard main-content">
            <div className="dashboard-header">
              <h1>Fulfillment Dashboard</h1>
              <UserFilter
                users={users}
                selectedUser={selectedUser}
                onUserChange={setSelectedUser}
                loading={loadingUsers}
                placeholder="All Users"
              />
            </div>

            <div className="dashboard-main">
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "100vh",
                }}
              >
                <div style={{ textAlign: "center" }}>
                  <Loader />
                  <div style={{ marginTop: "20px" }}>
                    Loading Fulfillment Dashboard...
                  </div>
                  {isLoading && <LoadingProgress progress={progress} />}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show error state - check if any query has an error
  const hasErrors = Object.values(error).some(
    (err) => err !== null && err !== false
  );
  if (hasErrors) {
    console.error("Dashboard errors:", error);
    return (
      <div style={{ height: "100vh", overflow: "auto" }}>
        <div className="container">
          <Sidebar activeItem="fulfillment" />
          <div className="content dashboard main-content">
            <div className="dashboard-header">
              <h1>Fulfillment Dashboard</h1>
              <UserFilter
                users={users}
                selectedUser={selectedUser}
                onUserChange={setSelectedUser}
                loading={loadingUsers}
                placeholder="All Users"
              />
            </div>

            <div className="dashboard-main">
              <ErrorMessage
                message="Failed to load fulfillment data. Please try again."
                onRetry={() => window.location.reload()}
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ height: "100vh", overflow: "auto" }}>
      <style jsx>
        {`
          .container {
            display: flex;
            min-height: 100vh;
            background-color: #f8f9fa;
          }

          .main-content {
            flex: 1;
            overflow-y: auto;
          }

          .skeleton-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
            gap: 20px;
            margin-bottom: 20px;
          }

          /* Cards Grid */
          .cards-grid {
            display: grid;
            gap: 4px !important;
            margin-bottom: 20px;
            width: 100%;
          }

          /* Mobile: 1 card per row */
          @media (max-width: 768px) {
            .cards-grid {
              grid-template-columns: 1fr !important;
            }
          }

          /* Desktop: max 4 per row */
          @media (min-width: 769px) {
            .cards-grid {
              grid-template-columns: repeat(4, 1fr) !important;
            }
          }

          /* Open Tasks Table */
          .open-tasks-table {
            margin-top: 32px;
            background: white;
            border-radius: 8px;
            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
            overflow: hidden;
          }

          .open-tasks-table-container {
            overflow-x: hidden;
            max-height: 400px;
            overflow-y: auto;
          }

          .open-tasks-table table {
            width: 100%;
            border-collapse: collapse;
            table-layout: fixed;
          }

          .open-tasks-table th,
          .open-tasks-table td {
            padding: 12px;
            text-align: left;
            border-bottom: 1px solid #dee2e6;
            overflow: hidden;
            text-overflow: ellipsis;
          }

          .open-tasks-table th {
            background-color: #f8f9fa;
            font-weight: 600;
            position: sticky;
            top: 0;
            z-index: 10;
          }

          .open-tasks-table tbody tr:hover {
            background-color: #f8f9fa;
            cursor: pointer;
          }

          /* Column widths */
          .open-tasks-table th:nth-child(1),
          .open-tasks-table td:nth-child(1) {
            width: 14%;
            white-space: nowrap;
          } /* Owner */
          .open-tasks-table th:nth-child(2),
          .open-tasks-table td:nth-child(2) {
            width: 14%;
            white-space: nowrap;
          } /* Account */
          .open-tasks-table th:nth-child(3),
          .open-tasks-table td:nth-child(3) {
            width: 14%;
            white-space: nowrap;
          } /* Subject */
          .open-tasks-table th:nth-child(4),
          .open-tasks-table td:nth-child(4) {
            width: 14%;
            white-space: nowrap;
          } /* Task Type */
          .open-tasks-table th:nth-child(5),
          .open-tasks-table td:nth-child(5) {
            width: 14%;
            white-space: nowrap;
          } /* Date */
          .open-tasks-table th:nth-child(6),
          .open-tasks-table td:nth-child(6) {
            width: 30%;
            white-space: normal;
            word-wrap: break-word;
          } /* Details */
        `}
      </style>
      <div className="container">
        {/* Show loading until ALL data is ready */}
        {!isAllDataReady && (
          <>
            {sidebar !== "0" ? <Sidebar activeItem="fulfillment" /> : null}
            <div className="content dashboard main-content">
              <div style={{ padding: "20px" }}>
                <h1 className="cs-title">Fulfillment Dashboard</h1>
                <LoadingProgress
                  current={progress.current}
                  total={progress.total}
                />
                <div className="skeleton-grid">
                  <SkeletonLoader type="card" count={4} />
                  <SkeletonLoader type="card" count={4} />
                </div>
              </div>
            </div>
          </>
        )}

        {isAllDataReady && (
          <>
            {sidebar !== "0" ? <Sidebar activeItem="fulfillment" /> : null}
            <div className="content dashboard main-content">
              {/* Header Section */}
              <div style={{ padding: "20px 20px 0 20px" }}>
                <div
                  style={{
                    marginBottom: "20px",
                    display: "flex",
                    alignItems: "center",
                    gap: "20px",
                  }}
                >
                  <h1 className="cs-title" style={{ margin: 0 }}>
                    Fulfillment Dashboard
                  </h1>
                  <UserFilter
                    users={users}
                    selectedUser={selectedUser}
                    onUserChange={handleUserChange}
                    loading={loadingUsers}
                    placeholder="All Users"
                  />
                </div>
              </div>

              {/* Status Cards Section */}
              <div style={{ padding: "0 20px 20px 20px" }}>
                <div className="cards-grid">
                  <SimpleStatCard
                    title="Websites Launched This Month"
                    value={data.websitesLaunchedThisMonth}
                    loading={false}
                    error={error.websitesLaunchedThisMonth}
                    onRetry={() => handleRetry("websitesLaunchedThisMonth")}
                  />

                  <SimpleStatCard
                    title="Websites In Progress"
                    value={data.websitesInProgress}
                    loading={false}
                    error={error.websitesInProgress}
                    onRetry={() => handleRetry("websitesInProgress")}
                    color="green"
                    tooltip="Sites not in 'Launched' or 'Pending Onboarding' with Launch Due Date this month"
                    style={{ padding: "16px 12px" }}
                    summary="Websites currently being built"
                  />

                  <SimpleStatCard
                    title="Websites with Roadblocks"
                    value={data.websitesWithRoadblocks}
                    loading={false}
                    error={error.websitesWithRoadblocks}
                    onRetry={() => handleRetry("websitesWithRoadblocks")}
                    color={
                      data.websitesWithRoadblocks?.count > 0 ? "red" : "green"
                    }
                    tooltip="Sites with status 'Waiting Client Info/Roadblock' this year"
                    style={{ padding: "16px 12px" }}
                    summary="Websites blocked by client dependencies"
                  />

                  <SimpleStatCard
                    title="Website Updates This Month"
                    value={data.websiteUpdatesThisMonth}
                    loading={false}
                    error={error.websiteUpdatesThisMonth}
                    onRetry={() => handleRetry("websiteUpdatesThisMonth")}
                    color="green"
                    tooltip="Website updates completed this month"
                    style={{ padding: "16px 12px" }}
                    summary="Website updates completed by team members"
                  />

                  <SimpleStatCard
                    title="Social Media Posts This Month"
                    value={data.socialMediaPostsThisMonth}
                    loading={false}
                    error={error.socialMediaPostsThisMonth}
                    onRetry={() => handleRetry("socialMediaPostsThisMonth")}
                    color="green"
                    tooltip="Social media posts completed this month"
                    style={{ padding: "16px 12px" }}
                    summary="Social media posts completed by team members"
                  />

                  <SimpleStatCard
                    title="Social Media Posts - Overdue"
                    value={data.socialMediaPostsOverdue}
                    loading={false}
                    error={error.socialMediaPostsOverdue}
                    onRetry={() => handleRetry("socialMediaPostsOverdue")}
                    color={
                      data.socialMediaPostsOverdue?.count > 0 ? "red" : "green"
                    }
                    tooltip="Social media posts past their scheduled date and not completed"
                    style={{ padding: "16px 12px" }}
                    summary="Social media posts requiring immediate attention"
                  />

                  <SimpleStatCard
                    title="PPC + Bing - Roadblocks"
                    value={data.ppcBingRoadblocks}
                    loading={false}
                    error={error.ppcBingRoadblocks}
                    onRetry={() => handleRetry("ppcBingRoadblocks")}
                    color={data.ppcBingRoadblocks?.count > 0 ? "red" : "green"}
                    tooltip="PPC and Bing campaigns with roadblock status"
                    style={{ padding: "16px 12px" }}
                    summary="PPC and Bing campaigns blocked by issues"
                  />

                  <SimpleStatCard
                    title="Social+ + Nextdoor - Roadblocks"
                    value={data.socialPlusNextdoorRoadblocks}
                    loading={false}
                    error={error.socialPlusNextdoorRoadblocks}
                    onRetry={() => handleRetry("socialPlusNextdoorRoadblocks")}
                    color={
                      data.socialPlusNextdoorRoadblocks?.count > 0
                        ? "red"
                        : "green"
                    }
                    tooltip="Social+ and Nextdoor accounts with roadblock status"
                    style={{ padding: "16px 12px" }}
                    summary="Social+ and Nextdoor accounts blocked by issues"
                  />

                  <SimpleStatCard
                    title="Content Uploaded This Month"
                    value={data.contentUploadedThisMonth}
                    loading={false}
                    error={error.contentUploadedThisMonth}
                    onRetry={() => handleRetry("contentUploadedThisMonth")}
                    color="green"
                    tooltip="Content upload tasks completed this month"
                    style={{ padding: "16px 12px" }}
                    summary="Content upload tasks completed by team members"
                  />

                  <SimpleStatCard
                    title="Content Completed This Month"
                    value={data.contentCompletedThisMonth}
                    loading={false}
                    error={error.contentCompletedThisMonth}
                    onRetry={() => handleRetry("contentCompletedThisMonth")}
                    color="green"
                    tooltip="Content tasks completed by Content Writers this month"
                    style={{ padding: "16px 12px" }}
                    summary="Content writing tasks completed by Content Writers"
                  />

                  <SimpleStatCard
                    title="Content Orders Status"
                    value={data.contentOrdersStatus}
                    loading={false}
                    error={error.contentOrdersStatus}
                    onRetry={() => handleRetry("contentOrdersStatus")}
                    color="green"
                    tooltip="Current month content orders by status"
                    style={{ padding: "16px 12px" }}
                    summary="All content orders for the current month"
                  />

                  <SimpleStatCard
                    title="Content Orders Overdue"
                    value={data.contentOrdersOverdue}
                    loading={false}
                    error={error.contentOrdersOverdue}
                    onRetry={() => handleRetry("contentOrdersOverdue")}
                    color={
                      data.contentOrdersOverdue?.count > 0 ? "red" : "green"
                    }
                    tooltip="Content orders past their due date and not completed"
                    style={{ padding: "16px 12px" }}
                    summary="Content orders requiring immediate attention"
                  />
                </div>

                {/* Open Tasks Table */}
                {openTasks && openTasks.length > 0 && (
                  <div className="open-tasks-table">
                    <div
                      style={{
                        padding: "20px 20px 10px 20px",
                        backgroundColor: "#f8f9fa",
                        borderBottom: "1px solid #dee2e6",
                      }}
                    >
                      <h4 style={{ margin: 0, color: "#333" }}>
                        Open Tasks{" "}
                        {selectedUser
                          ? `for ${
                              users.find((u) => u.id === selectedUser)?.name ||
                              "Selected User"
                            }`
                          : ""}
                      </h4>
                      <p
                        style={{
                          margin: "5px 0 0 0",
                          color: "#666",
                          fontSize: "14px",
                        }}
                      >
                        Click on any task to open in Salesforce
                      </p>
                    </div>

                    {/* Group tasks by user */}
                    {(() => {
                      const tasksByUser = {};
                      openTasks.forEach((task) => {
                        if (!tasksByUser[task.owner]) {
                          tasksByUser[task.owner] = [];
                        }
                        tasksByUser[task.owner].push(task);
                      });

                      return Object.entries(tasksByUser).map(
                        ([userName, userTasks], userIndex) => (
                          <div key={userName}>
                            <div
                              style={{
                                padding: "10px 20px",
                                backgroundColor: "#2b5597",
                                color: "white",
                                marginTop: userIndex === 0 ? "0" : "10px",
                                fontWeight: "600",
                                textTransform: "uppercase",
                              }}
                            >
                              {userName} - <b>{userTasks.length}</b> Tasks
                            </div>
                            <div className="open-tasks-table-container">
                              <table>
                                <thead>
                                  <tr>
                                    <th>Account</th>
                                    <th>Subject</th>
                                    <th>Task Type</th>
                                    <th>Date</th>
                                    <th>Details</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {userTasks.map((task) => (
                                    <tr
                                      key={task.id}
                                      onClick={() => {
                                        window.open(
                                          `https://gargleinc.lightning.force.com/${task.id}`,
                                          "_blank"
                                        );
                                      }}
                                    >
                                      <td>{task.account}</td>
                                      <td>{task.subject}</td>
                                      <td>{task.taskType}</td>
                                      <td>{task.date}</td>
                                      <td>{task.description}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )
                      );
                    })()}
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default FulfillmentDashboard;
